import { CheckCircle2, AlertTriangle, ShieldAlert, Quote, ListChecks, HandHelping, Sparkles } from "lucide-react";

const CONFIG = {
  sicuro: {
    bg: "bg-[#DCFCE7]",
    border: "border-[#22C55E]",
    text: "text-[#14532D]",
    icon: CheckCircle2,
    iconColor: "text-[#16A34A]",
    label: "Sembra Sicuro",
    description: "Questo messaggio sembra autentico.",
  },
  sospetto: {
    bg: "bg-[#FEF3C7]",
    border: "border-[#D97706]",
    text: "text-[#78350F]",
    icon: AlertTriangle,
    iconColor: "text-[#D97706]",
    label: "Attenzione, è Sospetto",
    description: "Ci sono alcuni segnali che ti consigliamo di valutare.",
  },
  truffa: {
    bg: "bg-[#FEE2E2]",
    border: "border-[#DC2626]",
    text: "text-[#7F1D1D]",
    icon: ShieldAlert,
    iconColor: "text-[#DC2626]",
    label: "È una Truffa!",
    description: "Non rispondere e non cliccare nulla. È pericoloso.",
  },
};

export default function ResultCard({ result }) {
  if (!result) return null;
  const cfg = CONFIG[result.risk_level] || CONFIG.sospetto;
  const Icon = cfg.icon;

  return (
    <div
      data-testid={`result-card-${result.risk_level}`}
      className={`${cfg.bg} border-4 ${cfg.border} rounded-[2rem] p-6 md:p-10 animate-fade-up`}
    >
      <div className="flex flex-col items-center text-center mb-6">
        <div className={`mb-4 ${cfg.iconColor}`}>
          <Icon size={72} strokeWidth={2.2} />
        </div>
        <h3
          data-testid="result-card-label"
          className={`font-heading text-3xl md:text-4xl font-extrabold ${cfg.text}`}
        >
          {cfg.label}
        </h3>
        <p className={`text-xl md:text-2xl mt-3 font-semibold ${cfg.text}`}>
          {result.titolo}
        </p>
      </div>

      <div className="bg-white/70 rounded-2xl p-5 md:p-7 mb-6">
        <p
          data-testid="result-card-explanation"
          className={`text-lg md:text-xl leading-relaxed ${cfg.text}`}
        >
          {result.spiegazione}
        </p>
      </div>

      {result.segnali && result.segnali.length > 0 && (
        <div className="bg-white/70 rounded-2xl p-5 md:p-7 mb-6">
          <h4 className={`flex items-center gap-3 font-heading text-xl md:text-2xl font-bold mb-4 ${cfg.text}`}>
            <ListChecks size={28} /> Cosa abbiamo notato
          </h4>
          <ul className="space-y-3">
            {result.segnali.map((s, i) => (
              <li
                key={`segnale-${i}-${s}`}
                data-testid={`result-segnale-${i}`}
                className={`flex items-start gap-3 text-lg md:text-xl ${cfg.text}`}
              >
                <span className={`mt-1 inline-block w-3 h-3 rounded-full ${cfg.iconColor.replace("text-", "bg-")}`} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.azioni && result.azioni.length > 0 && (
        <div className="bg-white/70 rounded-2xl p-5 md:p-7 mb-6">
          <h4 className={`flex items-center gap-3 font-heading text-xl md:text-2xl font-bold mb-4 ${cfg.text}`}>
            <HandHelping size={28} /> Cosa fare adesso
          </h4>
          <ol className="space-y-3">
            {result.azioni.map((a, i) => (
              <li
                key={`azione-${i}-${a}`}
                data-testid={`result-azione-${i}`}
                className={`flex items-start gap-4 text-lg md:text-xl ${cfg.text}`}
              >
                <span className={`flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-white ${cfg.iconColor.replace("text-", "bg-")}`}>
                  {i + 1}
                </span>
                <span className="pt-1">{a}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {result.estratto_testo && (
        <div className="bg-white/70 rounded-2xl p-5 md:p-7">
          <h4 className={`flex items-center gap-3 font-heading text-xl font-bold mb-3 ${cfg.text}`}>
            <Quote size={24} /> Testo letto dall'immagine
          </h4>
          <p
            data-testid="result-card-estratto"
            className={`text-base md:text-lg italic leading-relaxed whitespace-pre-wrap ${cfg.text}`}
          >
            "{result.estratto_testo}"
          </p>
        </div>
      )}

      {/* AI transparency notice — required by EU AI Act Art. 50 and Italian regs */}
      <div
        data-testid="result-ai-disclaimer"
        className="mt-6 bg-white/80 border-2 border-gray-200 rounded-2xl p-4 md:p-5"
      >
        <div className="flex items-start gap-3">
          <Sparkles size={22} className="text-[#1E3A8A] flex-shrink-0 mt-1" />
          <p className="text-sm md:text-base text-gray-700 leading-relaxed">
            <strong className="text-[#1E3A8A]">Risposta generata da intelligenza artificiale</strong>{" "}
            (Google Gemini 3) — è un orientamento, <strong>non una certezza assoluta</strong>.
            Nel dubbio chiedi sempre conferma a un familiare di fiducia, alla tua banca o alla Polizia
            Postale (06 4654 1).
          </p>
        </div>
      </div>
    </div>
  );
}
